// cspell:ignore healthcheck
import * as Sentry from '@sentry/react';
import axios, { AxiosError, AxiosProgressEvent, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { apiUrl } from '@/config';
import { CONTENT, getAutodocsTitle } from '@/constants/contents';

import { security } from './security';
import type {
  AgentConnection,
  AgentInstanceDetail,
  AgentInstanceRequest,
  AgentInstanceSummary,
  ClearAgentConnectionCredentialRequest,
  SaveAgentConnectionCredentialRequest,
  TestAgentConnectionRequest,
  TestAgentConnectionResponse,
} from './types/agentcore';
import type { AtlasWebPageResponse, GetAtlasPageParams } from './types/atlas';
import type {
  ByomModelsResponse,
  ByomStatus,
  CredentialCreateRequest,
  CredentialDiscovery,
  EnableTeamRequest,
  PriceSuggestion,
  PriceSuggestionParams,
  SetOverridesRequest,
  UpdateLimitsRequest,
} from './types/byom';
import { AssetUploadResponse, CodebaseVersionRequest, CodebaseVersionsResponse } from './types/codebase';
import { ContentDownloadResponse } from './types/content';
import { AssociatedSourcesResponse } from './types/documentSources';
import type {
  EstateBranchesResponse,
  EstateSummary,
  GetEstateBranchesParams,
  GetEstateSummaryParams,
} from './types/estate';
import {
  CreateGitProviderApp,
  GitHubAuthorizationResponse,
  GitProviderApp,
  GitProviderAppInstall,
} from './types/gitProvider';
import { GitRepo } from './types/github';
import type { InvestigationMetricsRange, InvestigationMetricsResponse } from './types/investigationMetrics';
import type { LabDisableConfirmation, LabsListResponse } from './types/labs';
import { DeleteDocumentParams, LibraryRecord, UpdateDocumentParams } from './types/library';
import {
  AddMachineSourcesRequest,
  AddMachineTeamsRequest,
  CreateMachineRequest,
  CreateMachineResponse,
  GetMachinesParams,
  Machine,
  MachineSourcesResponse,
  MachineSourcesResponseWithNumberOfFilters,
  MachineTeamsResponse,
  MachineTeamsResponseWithNumberOfFilters,
  MachinesResponse,
  MachinesResponseWithNumberOfFilters,
  RegenerateKeyRequest,
  RegenerateKeyResponse,
  RemoveMachineSourcesRequest,
  RemoveMachineTeamsRequest,
  UpdateMachineRequest,
  UpdateMachineSourcesRequest,
  UpdateMachineTeamsRequest,
} from './types/machines';
import type { MfaSettings } from './types/mfa';
import {
  BranchFilterConfig,
  CodebaseBranchesResponse,
  CodebaseCardResponse,
  ContentRecord,
  ContentResponse,
  FetchContentParams,
  FetchPrimaryAssetParams,
  FetchVersionParams,
  ListCodebaseBranchesParams,
  PrimaryAssetRecord,
  PrimaryAssetResponse,
  SourceAutodocItem,
  SourceAutodocsResponse,
  UsageEstimateResponse,
  VersionResponse,
} from './types/node';
import { OnboardingChecklist, SkippableStep } from './types/onboardingChecklist';
import {
  CreateInvitationsRequest,
  CreateInvitationsResponse,
  FetchOrganizationUsersParams,
  MeResponse,
  OrganizationInvitation,
  OrganizationMembersResponse,
  OrganizationMembersResponseWithNumberOfFilters,
  OrganizationVisibilityResponse,
  OrganizationsResponse,
  SignupRequest,
  SignupResponse,
  User,
  UserRole,
} from './types/organization';
import type {
  CreatePATRequest,
  CreatePATResponse,
  GetPATsParams,
  ListPATsResponse,
  ListPATsResponseWithNumberOfFilters,
  PatSettings,
  UpdatePatSettingsRequest,
} from './types/personal-access-tokens';
import { ContentSource, GetPageSourcesParams } from './types/source';
import type {
  AdminSourcesResponse,
  AdminSourcesResponseWithNumberOfFilters,
  AssignmentType,
  GetAdminSourcesParams,
  SourceRole,
  SourceTeamSortField,
  SourceTeamsResponseWithNumberOfFilters,
  SourceUserSortField,
  SourceUsersResponseWithNumberOfFilters,
  SourceVisibility,
  UserSourceSortField,
  UserSourcesResponse,
  UserSourcesResponseWithNumberOfFilters,
} from './types/sources';
import type { SourceTeamsResponse, SourceUsersResponse } from './types/sources';
import type {
  CreateSsoConnectionRequest,
  CreateSsoConnectionResponse,
  EnableScimResponse,
  ScimMappingResponse,
  SpMetadataResponse,
  SsoConnectionStatusResponse,
  TestScimConnectionRequest,
  TestScimConnectionResponse,
  UpdateScimMappingRequest,
} from './types/ssoScim';
import { Subscription } from './types/subscription';
import {
  SupportConversation,
  supportAgentReadinessSchema,
  supportAttachmentSchema,
  supportConversationDetailSchema,
  supportConversationFeedbackListSchema,
  supportConversationFeedbackSchema,
  supportConversationListSchema,
  supportConversationSchema,
} from './types/supportAgent';
import { AboutYouSurveyRequest, AboutYouSurveySubmittedResponse } from './types/survey';
import {
  AssociateTagWithContentParams,
  ContentTagsResponse,
  DeleteTagParams,
  Tag,
  TagGetParams,
  TagGetResponse,
  TagResult,
  UpdateTagParams,
} from './types/tags';
import {
  Team,
  TeamMemberSortField,
  TeamMembersResponse,
  TeamMembersResponseWithNumberOfFilters,
  TeamRole,
  TeamSortField,
  TeamSourceSortField,
  TeamSourcesResponse,
  TeamSourcesResponseWithNumberOfFilters,
  TeamWithEffectiveRole,
  TeamsResponse,
  TeamsResponseWithNumberOfFilters,
  UserTeamSortField,
  UserTeamsResponse,
  UserTeamsResponseWithNumberOfFilters,
} from './types/teams';
import { TelemetryStatusResponse } from './types/telemetry';
import type {
  AvailableProjectsResponse,
  CreateScopeRequest,
  CustomFieldOption,
  DeleteScopeResponse,
  JiraPairingCodeResponse,
  TicketProjectScope,
  TicketProjectScopeDetail,
  TicketProviderInstallation,
  UpdateScopeRequest,
} from './types/ticketProvider';
import {
  Charge,
  CodebaseUsageResponse,
  DailyUsageActivity,
  EngagementBreakdownResponse,
  EngagementMetrics,
  FetchChargesParams,
  MachineUsageResponse,
  ServiceUsageItem,
  ServiceUsageResponse,
  StickinessResponse,
  StickinessTrendPoint,
  ToolUsageResponse,
  ActivityResponse as UsageActivityResponse,
  UsageBalance,
  UsageTrackingSummary,
  UserUsageResponse,
} from './types/usage';

export const tagsLimit = 1000;

// Reusable params serializer function
const paramsSerializer = (params: any) => {
  const searchParams = new URLSearchParams();
  for (const key in params) {
    if (Array.isArray(params[key])) {
      searchParams.append(key, params[key].join(','));
    } else {
      if (params[key] !== undefined) {
        searchParams.append(key, params[key]);
      }
    }
  }
  return searchParams.toString();
};

// Create unauthenticated axios instance for public endpoints
const publicApi = axios.create({
  baseURL: apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer,
});

// Create default authenticated axios instance.
const api = axios.create({
  baseURL: apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer,
});

// Add request interceptor for authentication and Sentry tracing.
// Domain errors the UI treats as a normal state rather than a failure.
// Organizations that predate plans answer `subscription_not_found` on every
// page that reads the plan, so reporting it floods Sentry.
const EXPECTED_API_ERROR_CODES = new Set(['subscription_not_found']);

/** Axios failures that are expected or too noisy for error tracking (cancellations, transient client/network). */
export const shouldReportAxiosErrorToSentry = (error: AxiosError): boolean => {
  if (axios.isCancel(error)) {
    return false;
  }
  if (error.code === AxiosError.ERR_NETWORK) {
    return false;
  }
  const { code } = parseApiError(error, '');
  return !(code && EXPECTED_API_ERROR_CODES.has(code));
};

api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      const token = await security.getAccessTokenSilently()();
      if (!token) {
        throw new Error('No authentication token available');
      }
      config.headers.Authorization = `Bearer ${token}`;

      // Add Sentry trace headers for distributed tracing (frontend ↔ backend correlation)
      // Wrapped in its own try-catch to prevent Sentry SDK issues from triggering auth redirects
      try {
        const activeSpan = Sentry.getActiveSpan();
        if (activeSpan) {
          const sentryTraceHeader = Sentry.spanToTraceHeader(activeSpan);
          const sentryBaggageHeader = Sentry.spanToBaggageHeader(activeSpan);
          config.headers['sentry-trace'] = sentryTraceHeader;
          if (sentryBaggageHeader) {
            config.headers['baggage'] = sentryBaggageHeader;
          }
        }
      } catch (sentryError) {
        // Don't let Sentry errors trigger auth redirects - they shouldn't affect API requests.
        console.error(sentryError);
      }

      return config;
    } catch (error: any) {
      security.getLoginWithRedirect()({
        appState: { targetUrl: window.location.href },
      });
      throw new axios.CanceledError('Authentication required');
    }
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (!axios.isAxiosError(error) || shouldReportAxiosErrorToSentry(error)) {
      Sentry.captureException(error);
    } else if (error.code === AxiosError.ERR_NETWORK) {
      Sentry.addBreadcrumb({
        category: 'network',
        message: `Network error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
        level: 'warning',
      });
    }
    if (error.response?.status === 401) {
      // Domain errors (e.g. git_token_revoked) include a structured error code
      // in the response. Only redirect to login for session auth failures.
      const data = error.response?.data as Record<string, unknown> | undefined;
      const detail = data?.detail;
      const hasDomainErrorCode =
        detail && typeof detail === 'object' && typeof (detail as Record<string, unknown>).code === 'string';
      if (!hasDomainErrorCode) {
        security.getLoginWithRedirect()({
          appState: { targetUrl: window.location.href },
        });
      }
    }
    return Promise.reject(error);
  }
);

const AXIOS_STATUS_MESSAGE = /^Request failed with status code \d{3}$/;

// Parse axios or generic errors into a user-friendly string.
export const parseAxiosError = (error: unknown, defaultMsg: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    const detail = data?.detail;
    if (typeof detail === 'string' && detail.trim().length > 0) {
      return detail;
    }
    if (detail && typeof detail.message === 'string' && detail.message.trim().length > 0) {
      return detail.message;
    }
    if (Array.isArray(detail) && detail.length > 0) {
      const firstEntry = detail[0];
      if (firstEntry && typeof firstEntry.msg === 'string' && firstEntry.msg.trim().length > 0) {
        return firstEntry.msg;
      }
    }
    // Axios always sets this string for HTTP errors, which would otherwise
    // mask the caller-supplied fallback when the body has no usable detail.
    if (
      typeof error.message === 'string' &&
      error.message.trim().length > 0 &&
      !AXIOS_STATUS_MESSAGE.test(error.message)
    ) {
      return error.message;
    }
    return defaultMsg;
  }
  if (error instanceof Error && typeof error.message === 'string' && error.message.trim().length > 0) {
    return error.message;
  }
  return defaultMsg;
};

export interface PatternValidationError {
  field: 'allow_patterns' | 'block_patterns';
  index: number;
  message: string;
}

export const parsePatternValidationError = (error: unknown): PatternValidationError | null => {
  if (!axios.isAxiosError(error)) {
    return null;
  }
  const detail = error.response?.data?.detail;
  if (!Array.isArray(detail) || detail.length === 0) {
    return null;
  }
  const loc = detail[0]?.loc;
  if (!Array.isArray(loc) || loc.length < 2) {
    return null;
  }
  const index = loc[loc.length - 1];
  const field = loc[loc.length - 2];
  if (typeof index !== 'number' || !Number.isInteger(index)) {
    return null;
  }
  if (field !== 'allow_patterns' && field !== 'block_patterns') {
    return null;
  }
  const message = typeof detail[0]?.msg === 'string' ? detail[0].msg : '';
  return { field, index, message };
};

export interface ParsedApiError {
  code: string | undefined;
  context: Record<string, unknown>;
  httpStatus: number | undefined;
  message: string;
}

export const parseApiError = (error: unknown, defaultMsg: string): ParsedApiError => {
  const message = parseAxiosError(error, defaultMsg);
  let code: string | undefined;
  let context: Record<string, unknown> = {};
  let httpStatus: number | undefined;
  if (axios.isAxiosError(error)) {
    httpStatus = error.response?.status;
    const detail = error.response?.data?.detail;
    if (detail && typeof detail === 'object') {
      if (typeof detail.code === 'string') {
        code = detail.code;
      }
      if (detail.context && typeof detail.context === 'object') {
        context = detail.context as Record<string, unknown>;
      }
    }
  }
  return { code, context, httpStatus, message };
};

// API Functions
export const getPrimaryAsset = async (params: FetchPrimaryAssetParams) => {
  const response = await api.get<PrimaryAssetResponse>('/node/primary_assets', { params });
  return response.data;
};

export const getPageAssets = async (params: FetchPrimaryAssetParams) => {
  const response = await api.get<PrimaryAssetResponse>('/node/page_assets', { params });
  return response.data;
};

export const getCodebaseCard = async (params: FetchPrimaryAssetParams): Promise<CodebaseCardResponse> => {
  const response = await api.get<CodebaseCardResponse>('/codebase_card/', {
    params,
    baseURL: apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl,
  });

  // We need to add the provider to the response data.
  return {
    ...response.data,
    results:
      response.data.results?.map((result) => ({
        ...result,
        provider: result.version_control?.provider,
      })) ?? [],
  };
};

export const addFavorite = async (primaryAssetId: string): Promise<void> => {
  await api.post(`/favorites/${primaryAssetId}`, null, {
    baseURL: apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl,
  });
};

export const removeFavorite = async (primaryAssetId: string): Promise<void> => {
  await api.delete(`/favorites/${primaryAssetId}`, {
    baseURL: apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl,
  });
};

export const getVersions = async (params: FetchVersionParams) => {
  const response = await api.get<VersionResponse>('/node/versions', { params });
  return response.data;
};

export const getContent = async ({ include_content = true, ...rest }: FetchContentParams) => {
  const response = await api.get<ContentResponse>('/node/contents', { params: { include_content, ...rest } });
  return response.data;
};

export const getPageContents = async ({ include_content = true, ...rest }: FetchContentParams) => {
  const response = await api.get<ContentRecord>('/node/page_contents', { params: { include_content, ...rest } });
  return response.data;
};

export const getSourceAutodocs = async (params: {
  sourceId: string;
  versionNodeId: string;
  limit?: number;
  offset?: number;
  search?: string;
  sorting?: Array<{ id: string; desc: boolean }>;
  tag_ids?: string[];
  type?: 'autodoc' | 'page';
}): Promise<SourceAutodocsResponse> => {
  // Fetch autodocs from getContent. The version node may not exist (e.g. it was
  // deleted), in which case the API returns 404 — treat that as empty results.
  let contentResponse: ContentResponse;
  try {
    contentResponse = await getContent({
      version_node_id: params.versionNodeId,
      include_content: false,
      content_kind: [
        CONTENT.DEEP_CONTEXT_CHANGELOG,
        CONTENT.DEEP_CONTEXT_ARCHITECTURE,
        CONTENT.DEEP_CONTEXT_LLM_ONBOARDING,
        CONTENT.DEEP_CONTEXT_BESPOKE,
      ],
      limit: 1000, // Fetch all to do in-memory filtering
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      contentResponse = { results: [], total_count: 0 };
    } else {
      throw error;
    }
  }

  // Fetch pages from getPageAssets
  const primaryAssetResponse = await getPageAssets({
    limit: 1000, // Fetch all to do in-memory filtering
    offset: 0,
    ...(params.search ? { display_name__ilike: `%${params.search}%` } : {}),
    ...(params.tag_ids && params.tag_ids.length > 0 ? { tag_ids: params.tag_ids } : {}),
    document_source_ids: [params.sourceId],
  });

  // Transform content records to autodoc items
  const autodocItems: SourceAutodocItem[] = contentResponse.results.map((content) => {
    // For BESPOKE docs, use the registering user from misc_metadata
    const bespokeCreator =
      content.content_kind === CONTENT.DEEP_CONTEXT_BESPOKE && content.misc_metadata?.created_by_id
        ? {
            id: String(content.misc_metadata.created_by_id),
            full_name: String(content.misc_metadata.created_by_name ?? 'Unknown'),
            email: String(content.misc_metadata.created_by_email ?? ''),
          }
        : undefined;

    return {
      id: content.id,
      display_name: content.content_name || getAutodocsTitle(content.content_kind) || 'Untitled Autodoc',
      type: 'autodoc' as const,
      created_at: content.created_at,
      updated_at: content.updated_at,
      creator: bespokeCreator ?? content.version_node?.version?.creator,
      rootVersionNodeId: content.version_node?.id,
    };
  });

  // Transform primary asset records to page items
  const pageItems: SourceAutodocItem[] = primaryAssetResponse.results.map((asset) => ({
    id: asset.id,
    display_name: asset.display_name,
    type: 'page' as const,
    created_at: asset.created_at,
    updated_at: asset.updated_at,
    creator: asset.most_recent_version?.creator,
    tags: asset.tags,
    rootVersionNodeId: asset.most_recent_version?.root_version_node?.id,
  }));

  // Combine both lists
  let combinedItems = [...autodocItems, ...pageItems];

  // Apply type filter
  if (params.type) {
    combinedItems = combinedItems.filter((item) => item.type === params.type);
  }

  // Apply tag filter (for autodocs, since pages are already filtered via API)
  // FUTURE: Add support for tagging Autodocs?
  // Currently they don't have tags, so we don't need to filter them.
  // if (params.tag_ids && params.tag_ids.length > 0) {
  //   combinedItems = combinedItems.filter((item) => {
  //     if (item.type === 'page') {
  //       // Pages are already filtered via API
  //       return true;
  //     }
  //     // For autodocs, filter in memory
  //     const itemTagIds = (item.tags || []).map((tag: any) => tag.id);
  //     return params.tag_ids!.some((tagId) => itemTagIds.includes(tagId));
  //   });
  // }

  // Apply search filter
  if (params.search) {
    const searchQuery = params.search.toLowerCase();
    combinedItems = combinedItems.filter((item) => item.display_name.toLowerCase().includes(searchQuery));
  }

  // Apply sorting
  if (params.sorting && params.sorting.length > 0) {
    combinedItems.sort((a, b) => {
      for (const sort of params.sorting!) {
        let aValue: any;
        let bValue: any;

        // Handle different field types
        switch (sort.id) {
          case 'display_name':
            aValue = a.display_name.toLowerCase();
            bValue = b.display_name.toLowerCase();
            break;
          case 'created_at':
            aValue = new Date(a.created_at).getTime();
            bValue = new Date(b.created_at).getTime();
            break;
          case 'updated_at':
            aValue = new Date(a.updated_at).getTime();
            bValue = new Date(b.updated_at).getTime();
            break;
          case 'creator':
            aValue = (a.creator?.full_name || 'Driver').toLowerCase();
            bValue = (b.creator?.full_name || 'Driver').toLowerCase();
            break;
          default:
            aValue = a[sort.id as keyof SourceAutodocItem];
            bValue = b[sort.id as keyof SourceAutodocItem];
        }

        if (aValue < bValue) return sort.desc ? 1 : -1;
        if (aValue > bValue) return sort.desc ? -1 : 1;
      }
      return 0;
    });
  }

  // Apply pagination
  const limit = Number(params.limit) || combinedItems.length;
  const offset = Number(params.offset) || 0;
  const start = offset;
  const end = start + limit;
  const paginatedItems = combinedItems.slice(start, end);

  return {
    docs: paginatedItems,
    total: combinedItems.length,
  };
};

export const getContentTags = async ({ versionNodeId }: { versionNodeId: string }) => {
  const response = await api.get<ContentTagsResponse>(
    `/node/tags?primary_assets.branches.versions.root_version_node.id=${versionNodeId}`
  );
  return response.data;
};

export const getUsageBalance = async () => {
  const response = await api.get<UsageBalance>('/usage/balance');
  return response.data;
};

export const getCharges = async ({ limit = 50, offset = 0, sort_by, sort_direction }: FetchChargesParams) => {
  const response = await api.get<Charge[]>('/usage/charges', {
    params: { limit, offset, sort_by, sort_direction },
  });
  return response.data;
};

export const getContentDownloadUrl = async ({ versionNodeId }: { versionNodeId: string }) => {
  const response = await api.get<ContentDownloadResponse>(`/content/${versionNodeId}/download`);

  // Make a call to get the primary asset, basically just to get `effective_role`.
  // Ideally the backend should return this but adding it here for now.
  const pdfAssetData = await getPrimaryAsset({
    id: response.data.primary_asset_id,
    limit: 1,
    offset: 0,
  });

  if (!pdfAssetData.results?.[0]) {
    throw new Error('Primary asset not found');
  }

  return {
    ...response.data,
    effective_role: pdfAssetData.results[0].effective_role,
  };
};

export const updateContent = async (params: UpdateDocumentParams) => {
  const response = await api.put<LibraryRecord>(`/node/edit_page/${params.pageVersionNodeId}`, {
    content_name: params.name,
    content: params.content,
  });
  return response.data;
};

export const generateCodebases = async ({ version_ids }: { version_ids: string[] }) => {
  await api.post('/codebases/generate', { version_ids });
};

export const generatePrimaryAsset = async ({
  primary_asset_id,
  version_ids,
}: {
  primary_asset_id: string;
  version_ids: string[];
}) => {
  await api.post('/codebases/generate-primary-asset', { primary_asset_id, version_ids });
};

export const estimateCodebaseUsage = async (version_ids: string[]): Promise<UsageEstimateResponse> => {
  const response = await api.post<UsageEstimateResponse>('/codebases/estimate-usage', { version_ids });
  return response.data;
};

export const deleteDocument = async (params: DeleteDocumentParams) => {
  await api.delete(`/node/primary_assets/${params.id}`);
};

export const bulkDeleteSources = async ({ ids }: { ids: string[] }) => {
  const response = await api.post<{ deleted: string[]; errors: string[] }>(`/node/primary_assets/bulk-delete`, {
    ids,
  });
  return response.data;
};

export const bulkUpdateSourceVisibility = async ({
  ids,
  visibility,
}: {
  ids: string[];
  visibility: SourceVisibility;
}) => {
  const response = await api.post<{ updated: string[]; errors: string[] }>(`/node/primary_assets/bulk-visibility`, {
    ids,
    visibility,
  });
  return response.data;
};

export const updatePrimaryAsset = async ({
  id,
  settings,
}: {
  id: string;
  settings: {
    codebase_settings_auto_commit_docs?: boolean;
    vcs_auto_update_policy?: string;
    visibility?: SourceVisibility;
    branch_filter_config?: Omit<BranchFilterConfig, 'max_patterns'>;
  };
}) => {
  const response = await api.put<PrimaryAssetRecord>(`/node/primary_assets/${id}`, settings);
  return response.data;
};

export const getTags = async (params?: TagGetParams) => {
  const actualParams = { type: 'tag', limit: tagsLimit, offset: 0, name: undefined, ...params };
  if (actualParams?.name === '') {
    delete actualParams.name;
  }
  const response = await api.get<TagGetResponse>('/node/tags', { params: actualParams });
  return response.data;
};

export const getTagContents = async ({
  tagId,
  sort_by,
  sort_direction,
  limit = 20,
  offset = 0,
}: {
  tagId: string;
  sort_by?: string;
  sort_direction?: 'ASC' | 'DESC';
  limit?: number;
  offset?: number;
}) => {
  const response = await api.get<PrimaryAssetResponse>(
    `/node/primary_assets?tags.id=${tagId}&sort_by=${sort_by}&sort_direction=${sort_direction}&limit=${limit}&offset=${offset}`
  );
  return response.data;
};

export const createTag = async (body: Tag) => {
  const response = await api.post<TagResult>('/tags/', body);
  return response.data;
};

export const addSourceToDocument = async (params: ContentSource[]) => {
  await api.post('/node/document_sources/batch', params);
};

export const removeSourceFromDocument = async (params: ContentSource[]) => {
  await api.delete('/node/document_sources/batch', { data: params });
};

export const getPageSources = async ({ versionNodeId }: GetPageSourcesParams) => {
  const response = await api.get<AssociatedSourcesResponse>(
    `/node/page_sources?version_node_id=${versionNodeId}&limit=10000`
  );
  return response.data;
};

export const addTagToContent = async (body: AssociateTagWithContentParams) => {
  await api.post('/node/primary_asset_tags', {
    primary_asset_id: body.primaryAssetId,
    tag_id: body.tagId,
  });
};

export const updateTag = async (params: UpdateTagParams) => {
  await api.put(`/tags/${params.tagId}`, params.tag);
};

export const deleteTag = async (params: DeleteTagParams) => {
  await api.delete(`/tags/${params.tagId}`);
};

export const removeTagFromContent = async (body: AssociateTagWithContentParams) => {
  await api.delete(`/node/primary_asset_tags/${body.primaryAssetId}/${body.tagId}`);
};

export const uploadAsset = async ({ file_path }: { file_path: string }) => {
  const response = await api.post<AssetUploadResponse>('/upload/', { file_path });
  return response.data;
};

export const listCodebaseBranches = async (params: ListCodebaseBranchesParams) => {
  const { codebaseId, ...queryParams } = params;
  const response = await api.get<CodebaseBranchesResponse>(`/codebases/${codebaseId}/branches`, {
    params: queryParams,
    paramsSerializer: {
      indexes: null, // Send arrays as status=a&status=b (not status[0]=a)
    },
  });
  return response.data;
};

export const getCodebaseVersions = async ({ codebaseId, limit = 10, offset = 0 }: CodebaseVersionRequest) => {
  const response = await api.get<CodebaseVersionsResponse>(
    `/codebases/${codebaseId}/versions?limit=${limit}&offset=${offset}`
  );
  return response.data;
};

export const getMe = async () => {
  const response = await api.get<MeResponse>('/me/');
  return response.data;
};

export const getOrganizationVisibility = async () => {
  const response = await api.get<OrganizationVisibilityResponse>('/organization/settings/default-source-visibility');
  return response.data;
};

export const updateOrganizationVisibility = async ({ visibility }: { visibility: SourceVisibility }) => {
  const response = await api.put<OrganizationVisibilityResponse>('organization/settings/default-source-visibility', {
    default_source_visibility: visibility,
  });
  return response.data;
};

export const getOrganizationUsers = async (
  params?: FetchOrganizationUsersParams
): Promise<OrganizationMembersResponseWithNumberOfFilters> => {
  const response = await api.get<OrganizationMembersResponse>('/organization/users', {
    params,
  });

  let numberOfFilters = 0;
  if (params?.search) {
    numberOfFilters++;
  }
  if (params?.roles && params.roles.length > 0) {
    numberOfFilters++;
  }

  return {
    ...response.data,
    number_of_filters: numberOfFilters,
  };
};

export const getOrganizationUser = async ({ userId }: { userId: string }): Promise<User> => {
  const response = await api.get<User>(`/organization/users/${userId}`);
  return response.data;
};

export const createOrganizationInvitations = async (body: CreateInvitationsRequest) => {
  const response = await api.post<CreateInvitationsResponse>('/organization/invitations', body);
  return response.data;
};

export const createGitProviderApp = async (body: CreateGitProviderApp) => {
  const response = await api.post<GitProviderApp>('/git-provider/app', body);
  return response.data;
};

export const startGitHubAuthorization = async () => {
  // The browser-binding cookie rides on this response, so credentials must be allowed.
  const response = await api.post<GitHubAuthorizationResponse>('/git-provider/github/authorize', undefined, {
    withCredentials: true,
  });
  return response.data;
};

export const connectGitProviderAppRepos = async ({ app_id, repos }: { app_id: string; repos: GitRepo[] }) => {
  const response = await api.post<{ message: string }>(`/git-provider/app/${app_id}/connect-repos`, repos);
  return response.data;
};

export const saveGitProviderAccessToken = async ({
  app_id,
  name,
  token,
  token_type,
  metadata,
}: {
  app_id: string;
  name: string;
  token: string;
  token_type?: string;
  metadata?: Record<string, any>;
}) => {
  const payload: any = { name, token };
  if (token_type) {
    payload.token_type = token_type;
  }
  if (metadata) {
    payload.metadata = metadata;
    if (metadata.username) {
      payload.username = metadata.username;
    }
  }
  const response = await api.post<GitProviderAppInstall>(`/git-provider/app/${app_id}/token`, payload);
  return response.data;
};

export const getOrganizationInvitations = async () => {
  const response = await api.get<OrganizationInvitation[]>('/organization/invitations');
  return response.data;
};

export const removeUserFromOrganization = async (uid: string) => {
  const response = await api.delete<{ message: string }>(`/organization/users/${uid}`);
  return response.data;
};

export const bulkRemoveUsersFromOrganization = async ({ user_ids }: { user_ids: string[] }) => {
  const response = await api.post<{ deleted: string[]; errors: string[] }>(`/organization/users/bulk-delete`, {
    user_ids,
  });
  return response.data;
};

export const deleteInvitation = async (invitationId: string) => {
  const response = await api.delete<{ message: string }>(`/organization/invitations/${invitationId}`);
  return response.data;
};

export const bulkDeleteInvitations = async ({ ids }: { ids: string[] }) => {
  const response = await api.post<{ deleted: string[]; errors: string[] }>(`/organization/invitations/bulk-delete`, {
    ids,
  });
  return response.data;
};

export const deleteGitProviderApp = async ({ app_id }: { app_id: string }) => {
  const response = await api.delete<{ message: string }>(`/git-provider/app/${app_id}`);
  return response.data;
};

export const deleteGitProviderAppInstall = async ({ app_id, install_id }: { app_id: string; install_id: string }) => {
  const response = await api.delete<{ message: string }>(`/git-provider/app/${app_id}/installations/${install_id}`);
  return response.data;
};

/**
 * Update the role of multiple users in the organization.
 * @param members - The users to update the role of.
 * @returns The updated users.
 */
export const bulkUpdateUsersRole = async ({ members }: { members: { user_id: string; role: UserRole }[] }) => {
  await api.put(`/organization/users/role`, { members });
};

export const updateGitProviderGroupAccessToken = async ({
  app_id,
  install_id,
  token,
  token_type,
  metadata,
}: {
  app_id: string;
  install_id: string;
  token: string;
  token_type?: string;
  metadata?: Record<string, any>;
}) => {
  const payload: any = { token };
  if (token_type) {
    payload.token_type = token_type;
  }
  if (metadata) {
    payload.metadata = metadata;
  }

  const response = await api.put<GitProviderAppInstall>(
    `/git-provider/app/${app_id}/repos/${install_id}/token`,
    payload
  );
  return response.data;
};

export const resetPassword = async () => {
  const response = await api.put<{ message: string }>('/user/password');
  return response.data;
};

export const getOrganizations = async () => {
  const response = await api.get<OrganizationsResponse>('/user/organizations');
  return response.data;
};

export const getTeams = async ({
  limit = 30,
  offset = 0,
  search,
  source_id,
  user_id,
  machine_id,
  sort_by,
  sort_direction,
}: {
  limit?: number;
  offset?: number;
  search?: string;
  source_id?: string;
  user_id?: string;
  machine_id?: string;
  sort_by?: TeamSortField;
  sort_direction?: 'ASC' | 'DESC';
}): Promise<TeamsResponseWithNumberOfFilters> => {
  const response = await api.get<TeamsResponse>('/teams/', {
    params: { limit, offset, search, source_id, user_id, machine_id, sort_by, sort_direction },
  });

  let numberOfFilters = 0;
  if (search) {
    numberOfFilters++;
  }

  return {
    ...response.data,
    number_of_filters: numberOfFilters,
  };
};

export const createTeam = async ({
  name,
  members = [],
}: {
  name: string;
  members?: { user_id: string; role: TeamRole }[];
}): Promise<Team> => {
  const response = await api.post<Team>('/teams/', {
    name,
    members,
  });
  return response.data;
};

export const getTeam = async ({ teamId }: { teamId: string }): Promise<TeamWithEffectiveRole | null> => {
  try {
    const response = await api.get<TeamWithEffectiveRole>(`/teams/${teamId}`);
    return response.data;
  } catch (error) {
    return null;
  }
};

export const getTeamMembers = async (params: {
  teamId: string;
  limit?: number;
  offset?: number;
  roles?: TeamRole[];
  search?: string;
  sort_by?: TeamMemberSortField;
  sort_direction?: 'ASC' | 'DESC';
}): Promise<TeamMembersResponseWithNumberOfFilters> => {
  const response = await api.get<TeamMembersResponse>(`/teams/${params.teamId}/members`, {
    params: {
      limit: params.limit,
      offset: params.offset,
      roles: params.roles,
      search: params.search,
      sort_by: params.sort_by,
      sort_direction: params.sort_direction,
    },
  });

  let numberOfFilters = 0;
  if (params.search) {
    numberOfFilters++;
  }
  if (params.roles && params.roles.length > 0) {
    numberOfFilters++;
  }

  return {
    ...response.data,
    number_of_filters: numberOfFilters,
  };
};

export const getSourceUsers = async (params: {
  sourceId: string;
  limit?: number;
  offset?: number;
  roles?: SourceRole[];
  assignment_type?: AssignmentType;
  search?: string;
  sort_by?: SourceUserSortField;
  sort_direction?: 'ASC' | 'DESC';
}): Promise<SourceUsersResponseWithNumberOfFilters> => {
  const response = await api.get<SourceUsersResponse>(`/sources/${params.sourceId}/users`, {
    params: {
      limit: params.limit,
      offset: params.offset,
      roles: params.roles,
      assignment_type: params.assignment_type,
      search: params.search,
      sort_by: params.sort_by,
      sort_direction: params.sort_direction,
    },
  });

  let numberOfFilters = 0;
  if (params.search) {
    numberOfFilters++;
  }
  if (params.roles && params.roles.length > 0) {
    numberOfFilters++;
  }
  if (params.assignment_type) {
    numberOfFilters++;
  }

  return {
    ...response.data,
    users: response.data.users.map((user) => {
      const visibility: SourceVisibility = user.asset_org_role === 'asset_member' ? 'internal' : 'private';

      return {
        ...user,
        visibility,
      };
    }),
    number_of_filters: numberOfFilters,
  };
};

export const updateSourceUsers = async ({
  sourceId,
  users,
}: {
  sourceId: string;
  users: { user_id: string; role: SourceRole }[];
}) => {
  await api.put(`/sources/${sourceId}/users`, { users });
};

export const addSourceUsers = async ({
  sourceId,
  users,
}: {
  sourceId: string;
  users: { user_id: string; role: SourceRole }[];
}) => {
  await api.post(`/sources/${sourceId}/users`, { users });
};

export const removeSourceUsers = async ({ sourceId, users }: { sourceId: string; users: { user_id: string }[] }) => {
  await api.delete(`/sources/${sourceId}/users`, {
    data: { user_ids: users.map((uu) => uu.user_id) },
  });
};

export const getSourceTeams = async (params: {
  sourceId: string;
  limit?: number;
  offset?: number;
  roles?: SourceRole[];
  search?: string;
  sort_by?: SourceTeamSortField;
  sort_direction?: 'ASC' | 'DESC';
}): Promise<SourceTeamsResponseWithNumberOfFilters> => {
  const response = await api.get<SourceTeamsResponse>(`/sources/${params.sourceId}/teams`, {
    params: {
      limit: params.limit,
      offset: params.offset,
      roles: params.roles,
      search: params.search,
      sort_by: params.sort_by,
      sort_direction: params.sort_direction,
    },
  });

  let numberOfFilters = 0;
  if (params.search) {
    numberOfFilters++;
  }
  if (params.roles && params.roles.length > 0) {
    numberOfFilters++;
  }

  return {
    ...response.data,
    number_of_filters: numberOfFilters,
  };
};

export const updateSourceTeams = async ({
  sourceId,
  teams,
}: {
  sourceId: string;
  teams: { team_id: string; role: SourceRole }[];
}) => {
  await api.put(`/sources/${sourceId}/teams`, { teams });
};

export const addSourceTeams = async ({
  sourceId,
  teams,
}: {
  sourceId: string;
  teams: { team_id: string; role: SourceRole }[];
}) => {
  await api.post(`/sources/${sourceId}/teams`, { teams });
};

export const removeSourceTeams = async ({ sourceId, teams }: { sourceId: string; teams: { team_id: string }[] }) => {
  await api.delete(`/sources/${sourceId}/teams`, {
    data: { team_ids: teams.map((tt) => tt.team_id) },
  });
};

export const getTeamSources = async (params: {
  teamId: string;
  limit?: number;
  offset?: number;
  roles?: SourceRole[];
  visibilities?: SourceVisibility[];
  search?: string;
  sort_by?: TeamSourceSortField;
  sort_direction?: 'ASC' | 'DESC';
}): Promise<TeamSourcesResponseWithNumberOfFilters> => {
  const response = await api.get<TeamSourcesResponse>(`/teams/${params.teamId}/sources`, {
    params: {
      limit: params.limit,
      offset: params.offset,
      roles: params.roles,
      visibilities: params.visibilities,
      search: params.search,
      sort_by: params.sort_by,
      sort_direction: params.sort_direction,
    },
  });

  let numberOfFilters = 0;
  if (params.search) {
    numberOfFilters++;
  }
  if (params.roles && params.roles.length > 0) {
    numberOfFilters++;
  }
  if (params.visibilities && params.visibilities.length > 0) {
    numberOfFilters++;
  }

  return {
    ...response.data,
    number_of_filters: numberOfFilters,
  };
};

export const updateTeamSources = async ({
  teamId,
  sources,
}: {
  teamId: string;
  sources: { source_id: string; role: SourceRole }[];
}) => {
  await api.put(`/teams/${teamId}/sources`, {
    sources,
  });
};

export const addTeamSources = async ({
  teamId,
  sources,
}: {
  teamId: string;
  sources: { source_id: string; role: SourceRole }[];
}) => {
  await api.post(`/teams/${teamId}/sources`, {
    sources,
  });
};

export const removeTeamSources = async ({ teamId, source_ids }: { teamId: string; source_ids: string[] }) => {
  await api.delete(`/teams/${teamId}/sources`, {
    data: { source_ids },
  });
};

export const getUserTeams = async (params: {
  userId: string;
  limit?: number;
  offset?: number;
  roles?: TeamRole[];
  search?: string;
  sort_by?: UserTeamSortField;
  sort_direction?: 'ASC' | 'DESC';
}): Promise<UserTeamsResponseWithNumberOfFilters> => {
  const response = await api.get<UserTeamsResponse>(`/admin/users/${params.userId}/teams`, {
    params: {
      limit: params.limit,
      offset: params.offset,
      roles: params.roles,
      search: params.search,
      sort_by: params.sort_by,
      sort_direction: params.sort_direction,
    },
  });

  let numberOfFilters = 0;
  if (params.search) {
    numberOfFilters++;
  }
  if (params.roles && params.roles.length > 0) {
    numberOfFilters++;
  }

  return {
    ...response.data,
    number_of_filters: numberOfFilters,
  };
};

export const updateUserTeams = async ({
  userId,
  teams,
}: {
  userId: string;
  teams: { team_id: string; role: TeamRole }[];
}) => {
  await api.put(`/admin/users/${userId}/teams`, { teams });
};

export const addUserTeams = async ({
  user_id,
  teams,
}: {
  user_id: string;
  teams: { team_id: string; role: TeamRole }[];
}) => {
  await api.post(`/admin/users/${user_id}/teams`, { teams });
};

export const removeUserTeams = async ({ userId, team_ids }: { userId: string; team_ids: string[] }) => {
  await api.delete(`/admin/users/${userId}/teams`, {
    data: { team_ids },
  });
};

export const getUserSources = async (params: {
  userId: string;
  limit?: number;
  offset?: number;
  roles?: SourceRole[];
  assignment_type?: AssignmentType;
  search?: string;
  sort_by?: UserSourceSortField;
  sort_direction?: 'ASC' | 'DESC';
}): Promise<UserSourcesResponseWithNumberOfFilters> => {
  const response = await api.get<UserSourcesResponse>(`/admin/users/${params.userId}/sources`, {
    params: {
      limit: params.limit,
      offset: params.offset,
      roles: params.roles,
      assignment_type: params.assignment_type,
      search: params.search,
      sort_by: params.sort_by,
      sort_direction: params.sort_direction,
    },
  });

  let numberOfFilters = 0;
  if (params.search) {
    numberOfFilters++;
  }
  if (params.roles && params.roles.length > 0) {
    numberOfFilters++;
  }
  if (params.assignment_type) {
    numberOfFilters++;
  }

  return {
    ...response.data,
    sources: response.data.sources.map((source) => {
      const visibility: SourceVisibility = source.asset_org_role === 'asset_member' ? 'internal' : 'private';

      return {
        ...source,
        visibility,
      };
    }),
    number_of_filters: numberOfFilters,
  };
};

export const updateUserSources = async ({
  userId,
  sources,
}: {
  userId: string;
  sources: { source_id: string; role: SourceRole }[];
}) => {
  await api.put(`/admin/users/${userId}/sources`, { sources });
};

export const addUserSources = async ({
  userId,
  sources,
}: {
  userId: string;
  sources: { source_id: string; role: SourceRole }[];
}) => {
  await api.post(`/admin/users/${userId}/sources`, { sources });
};

export const removeUserSources = async ({ userId, source_ids }: { userId: string; source_ids: string[] }) => {
  await api.delete(`/admin/users/${userId}/sources`, {
    data: { source_ids },
  });
};

export const deleteTeam = async ({ teamId }: { teamId: string }) => {
  await api.delete(`/teams/${teamId}`);
};

export const bulkDeleteTeams = async ({ ids }: { ids: string[] }) => {
  const response = await api.post<{ deleted: string[]; errors: string[] }>(`/teams/bulk-delete`, { ids });
  return response.data;
};

export const updateTeam = async ({ teamId, name }: { teamId: string; name: string }) => {
  const response = await api.put<Team>(`/teams/${teamId}`, { name });
  return response.data;
};

export const updateTeamMembers = async ({
  teamId,
  members,
}: {
  teamId: string;
  members: { user_id: string; role: TeamRole }[];
}) => {
  await api.put(`/teams/${teamId}/members`, { members });
};

export const addTeamMembers = async ({
  teamId,
  members,
}: {
  teamId: string;
  members: {
    user_id: string;
    role: TeamRole;
  }[];
}) => {
  await api.post(`/teams/${teamId}/members`, { members });
};

export const removeTeamMembers = async ({ teamId, userIds }: { teamId: string; userIds: string[] }) => {
  await api.delete(`/teams/${teamId}/members`, { data: { user_ids: userIds } });
};

export const getSubscription = async () => {
  const response = await api.get<Subscription>('/subscription');
  return response.data;
};

export const getGitProviderApps = async () => {
  const response = await api.get<GitProviderApp[]>('/git-provider/app');
  return response.data;
};

export const getGitProviderAppInstalls = async ({ app_id }: { app_id: string }) => {
  const response = await api.get<GitProviderAppInstall[]>(`/git-provider/app/${app_id}/installations`);
  return response.data;
};

export const getGitProviderAppReposByInstallId = async ({
  app_id,
  install_id,
}: {
  app_id: string;
  install_id: string;
}) => {
  const response = await api.get<GitRepo[]>(`/git-provider/app/${app_id}/repos/${install_id}`);
  return response.data;
};

export const getGitProviderAppInstallWebhookInfo = async ({
  app_id,
  installation_id,
}: {
  app_id: string;
  installation_id: string;
}) => {
  const response = await api.get<any>(`/git-provider/app/${app_id}/installations/${installation_id}/webhook`);
  return response.data;
};

export const registerGitProviderWebhook = async ({
  app_id,
  installation_id,
}: {
  app_id: string;
  installation_id: string;
}) => {
  const response = await api.post<unknown>(
    `/git-provider/app/${app_id}/installations/${installation_id}/webhook/register`
  );
  return response.data;
};

export const unregisterGitProviderWebhook = async ({
  app_id,
  installation_id,
}: {
  app_id: string;
  installation_id: string;
}) => {
  const response = await api.delete<unknown>(
    `/git-provider/app/${app_id}/installations/${installation_id}/webhook/register`
  );
  return response.data;
};

export const signup = async (params: SignupRequest): Promise<SignupResponse> => {
  const response = await publicApi.post<SignupResponse>('/signup', params);
  return response.data;
};

export const submitAboutYouSurvey = async (params: AboutYouSurveyRequest) => {
  const payload = {
    plans_for_driver: params.plansForDriver,
    team_size: params.teamSize,
    type_of_work: params.typeOfWork,
    type_of_work_other: params.typeOfWorkOther ?? null,
    skipped: params.skipped ?? false,
  };
  await api.post('/about-you-survey', payload);
};

export const getAboutYouSurveySubmitted = async () => {
  const response = await api.get<AboutYouSurveySubmittedResponse>('/about-you-survey-submitted');
  return response.data;
};

export const getOnboardingChecklist = async () => {
  const response = await api.get<OnboardingChecklist>('/onboarding-checklist');
  return response.data;
};

export const skipOnboardingStep = async (step: SkippableStep) => {
  const response = await api.post<OnboardingChecklist>(`/onboarding-checklist/skip/${step}`);
  return response.data;
};

export const unskipOnboardingStep = async (step: SkippableStep) => {
  const response = await api.post<OnboardingChecklist>(`/onboarding-checklist/unskip/${step}`);
  return response.data;
};

export const getAdminSources = async (
  params: GetAdminSourcesParams = {}
): Promise<AdminSourcesResponseWithNumberOfFilters> => {
  const {
    limit = 20,
    offset = 0,
    search,
    kind,
    tag_ids,
    top_language,
    sort_by = 'updated_at',
    sort_direction = 'DESC',
    visibility,
    team_id,
    user_id,
    machine_id,
  } = params;

  const response = await api.get<AdminSourcesResponse>('/admin/sources/', {
    params: {
      limit,
      offset,
      search,
      kind,
      tag_ids,
      top_language,
      sort_by,
      sort_direction,
      visibility,
      team_id,
      user_id,
      machine_id,
    },
  });

  let numberOfFilters = 0;
  if (search) {
    numberOfFilters++;
  }
  if (kind && kind.length > 0) {
    numberOfFilters++;
  }
  if (tag_ids && tag_ids.length > 0) {
    numberOfFilters++;
  }
  if (top_language && top_language.length > 0) {
    numberOfFilters++;
  }
  if (visibility) {
    numberOfFilters++;
  }

  // 🐉🐉🐉
  // Fake the effective_role since the server doesn't return it.
  // Only assets admins can call this endpoint so for now we can assume they are asset admins.
  return {
    ...response.data,
    results: response.data.results.map((result) => ({
      ...result,
      effective_role: 'asset_admin',
    })),
    number_of_filters: numberOfFilters,
  };
};

// Estate API functions
export const getEstateSummary = (params: GetEstateSummaryParams = {}): Promise<EstateSummary> =>
  api.get<EstateSummary>('/estate/summary', { params }).then((r) => r.data);
export const getEstateBranches = (params: GetEstateBranchesParams = {}): Promise<EstateBranchesResponse> =>
  api
    .get<EstateBranchesResponse>('/estate/branches', { params, paramsSerializer: { indexes: null } })
    .then((r) => r.data);

// Atlas API functions
export const getAtlasPage = (params: GetAtlasPageParams): Promise<AtlasWebPageResponse> =>
  api.get<AtlasWebPageResponse>('/atlas/page', { params }).then((r) => r.data);

export const getBannerMessage = async (): Promise<{ message: string } | null> => {
  try {
    const response = await axios.get('/bannerMessage.json');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

// ===== Machine API functions =====

export const getMachines = async (params: GetMachinesParams = {}): Promise<MachinesResponseWithNumberOfFilters> => {
  const { limit = 30, offset = 0, search } = params;

  const response = await api.get<MachinesResponse>('/admin/machines/', {
    params: { limit, offset, search },
  });

  let numberOfFilters = 0;
  if (search) {
    numberOfFilters++;
  }

  return {
    ...response.data,
    number_of_filters: numberOfFilters,
  };
};

export const getMachine = async ({ machineId }: { machineId: string }): Promise<Machine | null> => {
  try {
    const response = await api.get<Machine>(`/admin/machines/${machineId}`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const createMachine = async (params: CreateMachineRequest): Promise<CreateMachineResponse> => {
  const response = await api.post<CreateMachineResponse>('/admin/machines/', params);
  return response.data;
};

export const updateMachine = async ({
  machineId,
  ...params
}: { machineId: string } & UpdateMachineRequest): Promise<Machine> => {
  const response = await api.patch<Machine>(`/admin/machines/${machineId}`, params);
  return response.data;
};

export const deleteMachine = async ({ machineId }: { machineId: string }) => {
  await api.delete(`/admin/machines/${machineId}`);
};

export const bulkDeleteMachines = async ({ ids }: { ids: string[] }) => {
  const response = await api.post<{ deleted: string[]; errors: string[] }>(`/admin/machines/bulk-delete`, { ids });
  return response.data;
};

export const regenerateMachineKey = async ({
  machineId,
  ...params
}: { machineId: string } & RegenerateKeyRequest): Promise<RegenerateKeyResponse> => {
  const response = await api.post<RegenerateKeyResponse>(`/admin/machines/${machineId}/regenerate-key`, params);
  return response.data;
};

// ===== Machine Teams API functions =====

export const getMachineTeams = async (params: {
  machineId: string;
  limit?: number;
  offset?: number;
  search?: string;
}): Promise<MachineTeamsResponseWithNumberOfFilters> => {
  const { machineId, limit = 30, offset = 0, search } = params;

  const response = await api.get<MachineTeamsResponse>(`/admin/machines/${machineId}/teams`, {
    params: { limit, offset, search },
  });

  let numberOfFilters = 0;
  if (search) {
    numberOfFilters++;
  }

  return {
    ...response.data,
    number_of_filters: numberOfFilters,
  };
};

export const addMachineTeams = async ({ machineId, teams }: { machineId: string } & AddMachineTeamsRequest) => {
  await api.post(`/admin/machines/${machineId}/teams`, { teams });
};

export const updateMachineTeams = async ({ machineId, teams }: { machineId: string } & UpdateMachineTeamsRequest) => {
  await api.put(`/admin/machines/${machineId}/teams`, { teams });
};

export const removeMachineTeams = async ({
  machineId,
  team_ids,
}: { machineId: string } & RemoveMachineTeamsRequest) => {
  await api.delete(`/admin/machines/${machineId}/teams`, {
    data: { team_ids },
  });
};

// ===== Machine Sources API functions =====

export const getMachineSources = async (params: {
  machineId: string;
  limit?: number;
  offset?: number;
  search?: string;
  assignment_type?: 'direct' | 'inherited';
}): Promise<MachineSourcesResponseWithNumberOfFilters> => {
  const { machineId, limit = 30, offset = 0, search, assignment_type } = params;

  const response = await api.get<MachineSourcesResponse>(`/admin/machines/${machineId}/sources`, {
    params: { limit, offset, search, assignment_type },
  });

  let numberOfFilters = 0;
  if (search) {
    numberOfFilters++;
  }
  if (assignment_type) {
    numberOfFilters++;
  }

  return {
    ...response.data,
    number_of_filters: numberOfFilters,
  };
};

export const addMachineSources = async ({ machineId, sources }: { machineId: string } & AddMachineSourcesRequest) => {
  await api.post(`/admin/machines/${machineId}/sources`, { sources });
};

export const updateMachineSources = async ({
  machineId,
  sources,
}: { machineId: string } & UpdateMachineSourcesRequest) => {
  await api.put(`/admin/machines/${machineId}/sources`, { sources });
};

export const removeMachineSources = async ({
  machineId,
  source_ids,
}: { machineId: string } & RemoveMachineSourcesRequest) => {
  await api.delete(`/admin/machines/${machineId}/sources`, {
    data: { source_ids },
  });
};

// ===== Labs =====

export const getLabs = async () => {
  const response = await api.get<LabsListResponse>('/labs/');
  return response.data;
};

export const updateLab = async ({
  key,
  enabled,
  confirmed,
}: {
  key: string;
  enabled: boolean;
  confirmed?: boolean;
}): Promise<LabDisableConfirmation | null> => {
  const response = await api.patch<LabDisableConfirmation>(`/labs/${key}`, { enabled, confirmed });
  if (response.status === 204) return null;
  return response.data;
};

// ===== Telemetry API functions =====

export const getTelemetryStatus = async (): Promise<TelemetryStatusResponse> => {
  const response = await api.get<TelemetryStatusResponse>('/telemetry/status');
  return response.data;
};

export const getInvestigationMetrics = (params: {
  range: InvestigationMetricsRange;
}): Promise<InvestigationMetricsResponse> =>
  api.get<InvestigationMetricsResponse>('/investigation-metrics', { params }).then((r) => r.data);

// =============================================================================
// Usage Tracking API
// =============================================================================

export const getUsageTrackingSummary = (params: {
  start_date?: string;
  end_date?: string;
  codebase_id?: string;
  user_id?: string;
  team_id?: string;
  machine_id?: string;
}): Promise<UsageTrackingSummary> =>
  api.get<UsageTrackingSummary>('/usage-tracking/summary', { params }).then((r) => r.data);

export const getUsageTrackingActivity = (params: {
  start_date?: string;
  end_date?: string;
  codebase_id?: string;
  user_id?: string;
  team_id?: string;
  machine_id?: string;
}): Promise<DailyUsageActivity[]> =>
  api.get<UsageActivityResponse>('/usage-tracking/activity', { params }).then((r) => r.data.data);

export const getUsageTrackingStickiness = (params: {
  start_date?: string;
  end_date?: string;
  codebase_id?: string;
  user_id?: string;
  team_id?: string;
  machine_id?: string;
}): Promise<StickinessTrendPoint[]> =>
  api.get<StickinessResponse>('/usage-tracking/stickiness', { params }).then((r) => r.data.data);

export const getUsageTrackingByTool = (params: {
  start_date?: string;
  end_date?: string;
  codebase_id?: string;
  user_id?: string;
  team_id?: string;
  machine_id?: string;
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_order?: string;
}): Promise<ToolUsageResponse> => api.get<ToolUsageResponse>('/usage-tracking/by-tool', { params }).then((r) => r.data);

export const getUsageTrackingByUser = (params: {
  start_date?: string;
  end_date?: string;
  codebase_id?: string;
  user_id?: string;
  team_id?: string;
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_order?: string;
}): Promise<UserUsageResponse> => api.get<UserUsageResponse>('/usage-tracking/by-user', { params }).then((r) => r.data);

export const getUsageTrackingByCodebase = (params: {
  start_date?: string;
  end_date?: string;
  codebase_id?: string;
  user_id?: string;
  team_id?: string;
  machine_id?: string;
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_order?: string;
}): Promise<CodebaseUsageResponse> =>
  api.get<CodebaseUsageResponse>('/usage-tracking/by-codebase', { params }).then((r) => r.data);

export const getUsageTrackingByService = (params: {
  start_date?: string;
  end_date?: string;
  codebase_id?: string;
  user_id?: string;
  team_id?: string;
  machine_id?: string;
  sort_by?: string;
  sort_order?: string;
}): Promise<ServiceUsageItem[]> =>
  api.get<ServiceUsageResponse>('/usage-tracking/by-service', { params }).then((r) => r.data.data);

export const getUsageTrackingEngagementMetrics = (params?: { end_date?: string }): Promise<EngagementMetrics> =>
  api.get<EngagementMetrics>('/usage-tracking/engagement-metrics', { params }).then((r) => r.data);

export const getUsageTrackingEngagement = (params: {
  limit?: number;
  offset?: number;
  codebase_id?: string;
  user_id?: string;
  team_id?: string;
  start_date?: string;
  end_date?: string;
  sort_by?: string;
  sort_order?: string;
}): Promise<EngagementBreakdownResponse> =>
  api.get<EngagementBreakdownResponse>('/usage-tracking/engagement', { params }).then((r) => r.data);

export const getUsageTrackingByMachine = (params: {
  start_date?: string;
  end_date?: string;
  codebase_id?: string;
  machine_id?: string;
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_order?: string;
}): Promise<MachineUsageResponse> =>
  api.get<MachineUsageResponse>('/usage-tracking/by-machine', { params }).then((r) => r.data);

// ===== Ticket Provider API functions =====

export const getTicketProviderInstallations = async () => {
  const response = await api.get<TicketProviderInstallation[]>('/ticket-providers/installations');
  return response.data;
};

export const deleteTicketProviderInstallation = async (id: string) => {
  const response = await api.delete<{ message: string }>(`/ticket-providers/installations/${id}`);
  return response.data;
};

export const generateJiraPairingCode = async () => {
  const response = await api.post<JiraPairingCodeResponse>('/ticket-providers/jira/pairing-code');
  return response.data;
};

// ===== Personal Access Token API functions =====

export const listPATs = async (params: GetPATsParams = {}): Promise<ListPATsResponseWithNumberOfFilters> => {
  const { limit = 30, offset = 0, search, status } = params;

  const response = await api.get<ListPATsResponse>('/personal-access-tokens/', {
    params: { limit, offset, search, status },
  });

  let numberOfFilters = 0;
  if (search) {
    numberOfFilters++;
  }
  if (status) {
    numberOfFilters++;
  }

  return {
    ...response.data,
    number_of_filters: numberOfFilters,
  };
};

export const createPAT = async (params: CreatePATRequest): Promise<CreatePATResponse> => {
  const response = await api.post<CreatePATResponse>('/personal-access-tokens/', params);
  return response.data;
};

export const deletePAT = async ({ tokenId }: { tokenId: string }): Promise<void> => {
  await api.delete(`/personal-access-tokens/${tokenId}`);
};

export const bulkDeletePATs = async ({ ids }: { ids: string[] }): Promise<{ deleted: string[]; errors: string[] }> => {
  const response = await api.post<{ deleted: string[]; errors: string[] }>('/personal-access-tokens/bulk-delete', {
    ids,
  });
  return response.data;
};

export const getPatSettings = async (): Promise<PatSettings> => {
  const response = await api.get<PatSettings>('/organization/settings/pat');
  return response.data;
};

export const updatePatSettings = async (params: UpdatePatSettingsRequest): Promise<PatSettings> => {
  const response = await api.put<PatSettings>('/organization/settings/pat', params);
  return response.data;
};

export const getMfaSettings = async (): Promise<MfaSettings> => {
  const response = await api.get<MfaSettings>('/organization/settings/mfa');
  return response.data;
};

export const updateMfaSettings = async (params: MfaSettings): Promise<MfaSettings> => {
  const response = await api.put<MfaSettings>('/organization/settings/mfa', params);
  return response.data;
};

// ===== Ticket Project Scope API functions =====

export const getScopes = async (installationId: string) => {
  const response = await api.get<TicketProjectScope[]>(`/ticket-providers/installations/${installationId}/scopes`);
  return response.data;
};

export const createScope = async (installationId: string, data: CreateScopeRequest) => {
  const response = await api.post<TicketProjectScope>(`/ticket-providers/installations/${installationId}/scopes`, data);
  return response.data;
};

export const getScopeDetail = async (scopeId: string) => {
  const response = await api.get<TicketProjectScopeDetail>(`/ticket-providers/scopes/${scopeId}`);
  return response.data;
};

export const getScopeCustomFieldNames = async (scopeId: string) => {
  const response = await api.get<CustomFieldOption[]>(`/ticket-providers/scopes/${scopeId}/custom-field-names`);
  return response.data;
};

export const updateScope = async (scopeId: string, data: UpdateScopeRequest) => {
  const response = await api.patch<TicketProjectScope>(`/ticket-providers/scopes/${scopeId}`, data);
  return response.data;
};

export const updateScopeStatus = async (scopeId: string, status: string) => {
  const response = await api.patch<TicketProjectScope>(`/ticket-providers/scopes/${scopeId}/status`, {
    status,
  });
  return response.data;
};

export const deleteScope = async (scopeId: string) => {
  const response = await api.delete<DeleteScopeResponse>(`/ticket-providers/scopes/${scopeId}`);
  return response.data;
};

export const getAvailableProjects = async (installationId: string) => {
  const response = await api.get<AvailableProjectsResponse>(
    `/ticket-providers/installations/${installationId}/available-projects`
  );
  return response.data;
};

export const refreshProjects = async (installationId: string) => {
  const response = await api.post<AvailableProjectsResponse>(
    `/ticket-providers/installations/${installationId}/refresh-projects`
  );
  return response.data;
};

// ===== SSO / SCIM self-service =====

export const getSsoConnectionStatus = async (): Promise<SsoConnectionStatusResponse> => {
  const response = await api.get<SsoConnectionStatusResponse>('/organization/settings/sso-scim/connection-status');
  return response.data;
};

export const createSsoConnection = async (params: CreateSsoConnectionRequest): Promise<CreateSsoConnectionResponse> => {
  const response = await api.post<CreateSsoConnectionResponse>(
    '/organization/settings/sso-scim/sso/connection',
    params
  );
  return response.data;
};

export const enableScim = async (): Promise<EnableScimResponse> => {
  const response = await api.post<EnableScimResponse>('/organization/settings/sso-scim/scim/enable');
  return response.data;
};

export const disableScim = async (): Promise<void> => {
  await api.post('/organization/settings/sso-scim/scim/disable');
};

export const testScimConnection = async (params: TestScimConnectionRequest): Promise<TestScimConnectionResponse> => {
  const response = await api.post<TestScimConnectionResponse>('/organization/settings/sso-scim/scim/test', params);
  return response.data;
};

export const getScimMapping = async (): Promise<ScimMappingResponse> => {
  const response = await api.get<ScimMappingResponse>('/organization/settings/sso-scim/scim/mapping');
  return response.data;
};

export const updateScimMapping = async (params: UpdateScimMappingRequest): Promise<ScimMappingResponse> => {
  const response = await api.put<ScimMappingResponse>('/organization/settings/sso-scim/scim/mapping', params);
  return response.data;
};

export const getSpMetadata = async (): Promise<SpMetadataResponse> => {
  const response = await api.get<SpMetadataResponse>('/organization/settings/sso-scim/sp-metadata');
  return response.data;
};

// ===== Bring Your Own Model (per-org LiteLLM team) =====

export const getByomStatus = async (): Promise<ByomStatus> => {
  const response = await api.get<ByomStatus>('/organization/settings/byom/status');
  return response.data;
};

export const getByomModels = async (): Promise<ByomModelsResponse> => {
  const response = await api.get<ByomModelsResponse>('/organization/settings/byom/models');
  return response.data;
};

export const enableByomTeam = async (params: EnableTeamRequest): Promise<ByomStatus> => {
  const response = await api.post<ByomStatus>('/organization/settings/byom/team', params);
  return response.data;
};

export const disableByomTeam = async (): Promise<void> => {
  await api.post('/organization/settings/byom/team/disable');
};

export const updateByomLimits = async (params: UpdateLimitsRequest): Promise<ByomStatus> => {
  const response = await api.put<ByomStatus>('/organization/settings/byom/limits', params);
  return response.data;
};

export const addByomCredential = async (params: CredentialCreateRequest): Promise<ByomStatus> => {
  const response = await api.post<ByomStatus>('/organization/settings/byom/credentials', params);
  return response.data;
};

export const deleteByomCredential = async (params: { name: string }): Promise<ByomStatus> => {
  const response = await api.delete<ByomStatus>(
    `/organization/settings/byom/credentials/${encodeURIComponent(params.name)}`
  );
  return response.data;
};

export const setByomOverrides = async (params: SetOverridesRequest): Promise<ByomStatus> => {
  const response = await api.put<ByomStatus>('/organization/settings/byom/overrides', params);
  return response.data;
};

export const getByomPriceSuggestion = async (params: PriceSuggestionParams): Promise<PriceSuggestion> => {
  const response = await api.get<PriceSuggestion>('/organization/settings/byom/price-suggestion', { params });
  return response.data;
};

export const getByomCredentialDiscovery = async (params: { name: string }): Promise<CredentialDiscovery> => {
  const response = await api.get<CredentialDiscovery>(
    `/organization/settings/byom/credentials/${encodeURIComponent(params.name)}/discovery`
  );
  return response.data;
};

// ===== AgentCore =====

export const getAgentInstances = async (): Promise<AgentInstanceSummary[]> => {
  const response = await api.get<AgentInstanceSummary[]>('/agent-instances');
  return response.data;
};

export const getAgentInstance = async (params: AgentInstanceRequest): Promise<AgentInstanceDetail> => {
  const response = await api.get<AgentInstanceDetail>(`/agent-instances/${encodeURIComponent(params.instance_id)}`);
  return response.data;
};

export const saveAgentConnectionCredential = async (
  params: SaveAgentConnectionCredentialRequest
): Promise<AgentConnection> => {
  const response = await api.put<AgentConnection>(
    `/agent-instances/${encodeURIComponent(params.instance_id)}/connections/${encodeURIComponent(params.connection_id)}/credential`,
    { value: params.value }
  );
  return response.data;
};

export const clearAgentConnectionCredential = async (
  params: ClearAgentConnectionCredentialRequest
): Promise<AgentConnection> => {
  const response = await api.delete<AgentConnection>(
    `/agent-instances/${encodeURIComponent(params.instance_id)}/connections/${encodeURIComponent(params.connection_id)}/credential`
  );
  return response.data;
};

export const testAgentConnection = async (params: TestAgentConnectionRequest): Promise<TestAgentConnectionResponse> => {
  const response = await api.post<TestAgentConnectionResponse>(
    `/agent-instances/${encodeURIComponent(params.instance_id)}/connections/${encodeURIComponent(params.connection_id)}/test`
  );
  return response.data;
};

// ===== Support Agent =====
const supportAgentConversationsPath = '/support-agent/conversations';

export const createSupportConversation = async (params: { title?: string | null } = {}) => {
  const response = await api.post(supportAgentConversationsPath, { title: params.title ?? null });
  return supportConversationSchema.parse(response.data);
};

export const listSupportConversations = async (params: { limit?: number; offset?: number } = {}) => {
  const response = await api.get(supportAgentConversationsPath, { params });
  return supportConversationListSchema.parse(response.data);
};

export const getSupportConversation = async (params: { conversationId: string }) => {
  const response = await api.get(`${supportAgentConversationsPath}/${encodeURIComponent(params.conversationId)}`);
  return supportConversationDetailSchema.parse(response.data);
};

export const deleteSupportConversation = async (params: { conversationId: string }): Promise<void> => {
  await api.delete(`${supportAgentConversationsPath}/${encodeURIComponent(params.conversationId)}`);
};

/**
 * Continue a conversation whose agent was replaced (410 `support_agent_replaced`)
 * in a new one: same ticket, transcript and report copied, bound to the agent
 * that serves now. Asking again returns the same successor.
 */
export const startSupportConversationSuccessor = async (params: { conversationId: string }) => {
  const response = await api.post(
    `${supportAgentConversationsPath}/${encodeURIComponent(params.conversationId)}/successor`
  );
  return supportConversationSchema.parse(response.data);
};

export const updateSupportConversation = async (params: {
  conversationId: string;
  status?: SupportConversation['status'];
  ticketId?: string | null;
  title?: string;
}) => {
  const { conversationId, status, ticketId, title } = params;
  // The server rejects an empty body and treats an omitted field as unchanged,
  // so only what the caller actually passed goes on the wire. `ticketId: null`
  // is a deliberate clear and must survive.
  const body: { status?: SupportConversation['status']; ticket_id?: string | null; title?: string } = {};
  if (status !== undefined) {
    body.status = status;
  }
  if (ticketId !== undefined) {
    body.ticket_id = ticketId;
  }
  if (title !== undefined) {
    body.title = title;
  }
  const response = await api.patch(`${supportAgentConversationsPath}/${encodeURIComponent(conversationId)}`, body);
  return supportConversationSchema.parse(response.data);
};

export const getSupportAgentReadiness = async () => {
  const response = await api.get('/support-agent/readiness');
  return supportAgentReadinessSchema.parse(response.data);
};

const supportAgentAttachmentsPath = '/support-agent/attachments';

/**
 * Stores one file for a later turn to carry. The body is multipart, declared
 * as such so axios hands the FormData to the browser untouched: with the
 * instance's JSON default it would serialise the form to JSON instead.
 */
export const uploadSupportAttachment = async (params: {
  file: File;
  onUploadProgress?: (event: AxiosProgressEvent) => void;
  signal?: AbortSignal;
}) => {
  const { file, onUploadProgress, signal } = params;
  const body = new FormData();
  body.append('file', file, file.name);
  const response = await api.post(supportAgentAttachmentsPath, body, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
    signal,
  });
  return supportAttachmentSchema.parse(response.data);
};

const supportConversationFeedbackPath = (conversationId: string) =>
  `${supportAgentConversationsPath}/${encodeURIComponent(conversationId)}/feedback`;

export const submitSupportConversationFeedback = async (params: {
  conversationId: string;
  rating: string;
  comment?: string | null;
}) => {
  const response = await api.post(supportConversationFeedbackPath(params.conversationId), {
    rating: params.rating,
    comment: params.comment ?? null,
  });
  return supportConversationFeedbackSchema.parse(response.data);
};

export const listSupportConversationFeedback = async (params: { conversationId: string }) => {
  const response = await api.get(supportConversationFeedbackPath(params.conversationId));
  return supportConversationFeedbackListSchema.parse(response.data);
};
